import { t } from 'i18next'
import { HelperService } from '../../services/helper.service'
import QuizService from '../../services/QuizService'
import { capitalizeFirstLetter } from '../../utils/appUtils'

const helperService = new HelperService()
const quizService = new QuizService()

export const getAllQuizzes = (params: any) => async () => {
  try {
    const response = await quizService.getAllQuizzes(params)
    return Promise.resolve(response.data.data.quizzes)
  } catch (error: any) {
    return Promise.reject([])
  }
}

export const createQuizz = (params: any) => async () => {
  try {
    const response = await quizService.createQuizz(params)
    return Promise.resolve(response.data.data.quiz)
  } catch (error: any) {
    return Promise.reject([])
  }
}

export const createQuizButtons = (params: any) => async () => {
  try {
    const response = await quizService.createQuizButtons(params)
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    return Promise.reject([])
  }
}

export const getQuizAlerts = async (quizId: any) => {
  try {
    const response = await quizService.getQuizAlerts(quizId)
    return response.data.data
  } catch (error: any) {
    return []
  }
}

export const updateQuizAlerts = async (quizId: any, data: any) => {
  try {
    const response = await quizService.updateQuizAlerts(quizId, data)
    return response.data.data
  } catch (error: any) {
    return []
  }
}

export const getAllQuizSubmissions =
  (quizId: any, filtersData: any) => async () => {
    try {
      const response = await quizService.getAllQuizSubmissions(
        quizId,
        filtersData,
      )
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      return Promise.reject([])
    }
  }

export const updateSubmissionsAcknowledge =
  (quizId: any, submissionId: any, data: any) => async () => {
    try {
      const response = await quizService.updateSubmissionsAcknowledge(
        quizId,
        submissionId,
        data,
      )
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      return Promise.reject([])
    }
  }

export const getSubmissionsBySubmissionId =
  (quizId: any, submissionId: any) => async () => {
    try {
      const response = await quizService.getSubmissionsBySubmissionId(
        quizId,
        submissionId,
      )
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      return Promise.reject([])
    }
  }

export const getSubmissionsReviewsByReviewId =
  (reviewId: string) => async () => {
    try {
      const response =
        await quizService.getSubmissionsReviewsByReviewId(reviewId)
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      return Promise.reject([])
    }
  }

export const getQuizFeatures = () => async () => {
  try {
    const response = await quizService.getQuizFeatures()
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    return Promise.reject([])
  }
}

export const connectToCanvas = () => async () => {
  try {
    const response = await quizService.connectToCanvas()
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    helperService.showToaster(
      error.response.status === 400
        ? 'Integration with Canvas LMS is not configured for this agent.'
        : error.response.data.status.message,
    )
    return Promise.reject([])
  }
}

export const connectToOauth2Lms =
  (lmsCode: string, requests_new_state: boolean = false) =>
  async () => {
    try {
      const response = await quizService.connectToOauth2Lms(
        lmsCode.toLowerCase(),
        requests_new_state,
      )
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      helperService.showToaster(
        error.response.status === 400
          ? `Integration with ${capitalizeFirstLetter(lmsCode)} LMS is not configured for this agent.`
          : error.response.data.status.message,
      )
      return Promise.reject([])
    }
  }

export const getLearningSubmissionsReviews =
  (quizId: string, status: string = null, filtersData: any = null) =>
  async () => {
    try {
      const response = await quizService.getLearningSubmissionsReviews(
        quizId,
        status,
        filtersData,
      )
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      return Promise.reject([])
    }
  }

export const updateReviews = (reviewId: string) => async () => {
  try {
    const response = await quizService.updateReviews(reviewId)
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    return Promise.reject([])
  }
}

export const getIntegrationCurrentCourses =
  (provider: string = 'canvas', isLinked?: boolean) =>
  async () => {
    try {
      const response = await quizService.getIntegrationCourses(
        provider,
        isLinked,
      )
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      return Promise.reject(error.response.data)
    }
  }

export const getIntegrationCourses =
  (provider: string = 'canvas', integrationId?: string) =>
  async () => {
    try {
      const response = await quizService.getIntegrationCourses(
        provider,
        false,
        integrationId,
      )
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      return Promise.reject(error.response.data)
    }
  }

export const getAssessmentsBasedOnCourses = (courseId: string) => async () => {
  try {
    const response = await quizService.getAssessmentsBasedOnCourses(courseId)
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    return Promise.reject([])
  }
}

export const sendScoresToCanvas = (data: any) => async () => {
  try {
    const response = await quizService.submitAssessmentForCanvas(data)
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    return Promise.reject([])
  }
}

export const downloadScoresForBrightspace = (data: any) => async () => {
  try {
    const response = await quizService.downloadScoresForBrightspace(data)
    return Promise.resolve(response.data)
  } catch (error: any) {
    return Promise.reject([])
  }
}

export const updateQuiz =
  (quizId: string, data: any, hideToast: boolean = false) =>
  async () => {
    try {
      const response = await quizService.updateQuiz(quizId, data)
      !hideToast && helperService.showToaster('Quiz saved.', 'success')
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      helperService.showToaster(error.response.data.status.message, 'error')
      return Promise.reject([])
    }
  }

export const deleteQuiz = (quizId: string, isArchive?: boolean) => async () => {
  try {
    const response = await quizService.deleteQuiz(quizId, isArchive)
    helperService.showToaster('Quiz Deleted.', 'success')
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    return Promise.reject([])
  }
}

export const restoreQuiz = (quizId: string) => async () => {
  try {
    const response = await quizService.restoreQuiz(quizId)
    helperService.showToaster('Quiz is restored.', 'success')
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    return Promise.reject([])
  }
}

export const getLinkedQuestionsBasedOnQuizAndQuestions =
  (quizId: string) => async () => {
    try {
      const response =
        await quizService.getLinkedQuestionsBasedOnQuizAndQuestions(quizId)
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      return Promise.reject([])
    }
  }

export const getLinkedIntentsBasedOnQuizAndQuestions =
  (quizId: string, questionId: string) => async () => {
    try {
      const response =
        await quizService.getLinkedIntentsBasedOnQuizAndQuestions(
          quizId,
          questionId,
        )
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      return Promise.reject([])
    }
  }

export const getAllQuestionBank = (params: any) => async () => {
  try {
    const response = await quizService.getAllQuestionBank(params)
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    return Promise.reject([])
  }
}

export const getAllQuestionBankNoPaging = (params: any) => async () => {
  try {
    const response = await quizService.getAllQuestionBank(params)
    return Promise.resolve(response.data.data.quiz_questions)
  } catch (error: any) {
    return Promise.reject([])
  }
}

export const deleteQuestionBankById =
  (questionId: any, isArchive: boolean) => async () => {
    try {
      const response = await quizService.deleteQuestionBankById(
        questionId,
        isArchive,
      )
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      helperService.showToaster(
        t('Something went wrong while deleting question.'),
      )
      return Promise.reject([])
    }
  }

export const restoreQuestion = (questionId: string) => async () => {
  try {
    const response = await quizService.restoreQuestion(questionId)
    helperService.showToaster(t('Restored successfully.'), 'success')
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    helperService.showToaster(
      t('Something went wrong while deleting question.'),
    )
    return Promise.reject([])
  }
}

export const generateQuestionBank =
  (data: any, showPromt: boolean = true) =>
  async () => {
    try {
      const response = await quizService.generateQuestionBank(data)
      if (showPromt) {
        helperService.showToaster(t('Updated successfully.'), 'success')
      }
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      helperService.showToaster(
        error.response.data.status.message
          ? error.response.data.status.message
          : t('Something went wrong while generating question.'),
      )
      return Promise.reject([])
    }
  }

export const generateQuestionBankV2 =
  (quizId: string, data: any, showPromt: boolean = true) =>
  async () => {
    try {
      const response = await quizService.generateQuestionBankV2(quizId, data)
      if (showPromt) {
        helperService.showToaster(t('Updated successfully.'), 'success')
      }
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      helperService.showToaster(
        error.response.data.status.message
          ? error.response.data.status.message
          : t('Something went wrong while generating question.'),
      )
      return Promise.reject([])
    }
  }

export const updateQuestionFromQuestionBank =
  (questionId: string, data: any, showPromt: boolean = true) =>
  async () => {
    try {
      const response = await quizService.updateQuestionFromQuestionBank(
        questionId,
        data,
      )
      if (showPromt) {
        helperService.showToaster(t('Updated successfully.'), 'success')
      }
      return Promise.resolve(response.data.data)
    } catch (error: any) {
      helperService.showToaster(
        t('Something went wrong while generating question.'),
      )
      return Promise.reject([])
    }
  }

export const getQuizQuestionBankById = (questionId: string) => async () => {
  try {
    const response = await quizService.getQuizQuestionById(questionId)
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    helperService.showToaster(
      t('Something went wrong while generating question.'),
    )
    return Promise.reject([])
  }
}

export const deleteQuizQuestionBankById = (questionId: string) => async () => {
  try {
    const response = await quizService.deleteQuizQuestionBankById(questionId)
    helperService.showToaster(t('Question deleted successfully.'), 'success')
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    helperService.showToaster(
      t('Something went wrong while generating question.'),
    )
    return Promise.reject([])
  }
}

export const saveQuizQuestionsBasedOnFile = (payload: any) => async () => {
  try {
    const response = await quizService.saveQuizQuestionsBasedOnFile(payload)
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    helperService.showToaster(
      t('Something went wrong while fetching questions.'),
    )
    return Promise.reject([])
  }
}

export const getMcqDistractors = (payload: any) => async () => {
  try {
    const response = await quizService.getMcqDistractors(payload)
    if (response?.data?.data && response.data.data.length === 0) {
      helperService.showToaster(
        t('We are unable to generate any more distractors.'),
        'success',
      )
    }
    return Promise.resolve(response.data.data)
  } catch (error: any) {
    helperService.showToaster(t('Unable to generate any more distractors.'))
    return Promise.reject([])
  }
}
